import { useEffect, useRef } from "react";
import { Call, Device } from "@twilio/voice-sdk";
import { twilioAudioTokenRefresh } from "config/constant";
import { ClientErrorTypes, SessionStatus, TwilioAudioCallEvent } from "globals/enums";
import { audioTokenApi } from "services/audio-token";
import { handleError } from "helpers/sentry";
import { useAppDispatch, useAppSelector } from "store/store";
import { setProctorCandidateChat, setTwilioAudioCallStatus } from "store/app.slice";
import {
  clearTwilioAudioDevice,
  getTwilioAudioDevice,
  setTwilioAudioDevice
} from "services/twilioAudioDevice";

let moduleIsInitializing = false;
let moduleTeardownComplete = false;

export const resetTwilioCallModuleState = () => {
  moduleIsInitializing = false;
  moduleTeardownComplete = false;
};

const useTwilioCall = () => {
  const dispatch = useAppDispatch();
  const sessionDetails = useAppSelector((state: any) => state.app.session);
  const isAudioConfigurationEnabled = useAppSelector((state: any) => state.app.configuration.audio_call);
  const sessionStatus = useAppSelector((state: any) => state.app.sessionStatus);
  const deviceRef = useRef<Device | null>(null);
  const activeCallRef = useRef<Call | null>(null);
  const acceptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDestroyingRef = useRef(false);
  const lastSessionIdRef = useRef<number | string | null>(null);

  const [getAudioToken] = audioTokenApi.endpoints.getAudioToken.useLazyQuery();

  const updateTwilioAudioCallStatus = (status: TwilioAudioCallEvent) => {
    dispatch(setTwilioAudioCallStatus({ twilio_audio_call_status: status }));
  };

  const clearAcceptTimer = () => {
    if (acceptTimerRef.current !== null) {
      clearTimeout(acceptTimerRef.current);
      acceptTimerRef.current = null;
    }
  };

  const teardownTwilioAudio = () => {
    if (isDestroyingRef.current) {
      return;
    }

    isDestroyingRef.current = true;
    moduleTeardownComplete = true;
    moduleIsInitializing = false;

    const activeCall = activeCallRef.current;
    const device = deviceRef.current || getTwilioAudioDevice();

    clearAcceptTimer();
    activeCallRef.current = null;

    try {
      activeCall?.disconnect();
    } catch (error: unknown) {
      handleError(error, ClientErrorTypes.AudioCallInitError);
    }

    try {
      device?.disconnectAll();
    } catch (error: unknown) {
      handleError(error, ClientErrorTypes.AudioCallInitError);
    }

    try {
      device?.destroy();
    } catch (error: unknown) {
      handleError(error, ClientErrorTypes.AudioCallInitError);
    } finally {
      deviceRef.current = null;
      clearTwilioAudioDevice(device);
      updateTwilioAudioCallStatus(TwilioAudioCallEvent.Disconnect);
    }
  };

  const startTwilioAudioCall = async (token: string) => {
    try {
      const device = new Device(token, { tokenRefreshMs: twilioAudioTokenRefresh });
      deviceRef.current = device;
      setTwilioAudioDevice(device);
      device.register();

      device.on(TwilioAudioCallEvent.Unregistered, () => {
        if (isDestroyingRef.current || moduleTeardownComplete) {
          deviceRef.current = null;
          clearTwilioAudioDevice(device);
        }
      });

      // Track the most-recently-received incoming call and its pending accept timer.
      // The Device can re-deliver the same call (new Call object) if it re-registers
      // mid-ring (token refresh / WebSocket reconnect). Without these guards both
      // timers fire simultaneously: the first accept succeeds, the second is rejected
      // by Twilio with an immediate disconnect that reverts the UI to green.
      device.on(TwilioAudioCallEvent.Incoming, async (incomingCall: Call) => {
        // Cancel any pending auto-accept from the previous delivery of this call
        clearAcceptTimer();

        activeCallRef.current = incomingCall;

        dispatch(
          setTwilioAudioCallStatus({
            twilio_audio_call_status: TwilioAudioCallEvent.Incoming
          })
        );
        dispatch(setProctorCandidateChat({ showProctorCandidateChat: true }));

        acceptTimerRef.current = setTimeout(() => {
          acceptTimerRef.current = null;
          incomingCall.accept();
        }, 3000);

        incomingCall.on(TwilioAudioCallEvent.Accept, () => {
          if (activeCallRef.current !== incomingCall) return;
          updateTwilioAudioCallStatus(TwilioAudioCallEvent.Accept);
        });

        incomingCall.on(TwilioAudioCallEvent.Reconnecting, () => {
          if (activeCallRef.current !== incomingCall) return;
          // Suppress — will be resolved by 'reconnected' or 'disconnect'
        });

        incomingCall.on(TwilioAudioCallEvent.Reconnected, () => {
          if (activeCallRef.current !== incomingCall) return;
          updateTwilioAudioCallStatus(TwilioAudioCallEvent.Accept);
        });

        incomingCall.on(TwilioAudioCallEvent.Disconnect, () => {
          if (activeCallRef.current !== incomingCall) return;
          clearAcceptTimer();
          activeCallRef.current = null;
          updateTwilioAudioCallStatus(TwilioAudioCallEvent.Disconnect);
        });
      });

      device.on(TwilioAudioCallEvent.TokenWillExpire, async () => {
        const response = await getAudioToken();
        const newToken = response.data?.token as string;
        device.updateToken(newToken);
      });

      device.on(TwilioAudioCallEvent.Error, (twilioError) => {
        console.error(twilioError);
      });
    } catch (error: unknown) {
      handleError(error, ClientErrorTypes.AudioCallInitError);
    }
  };

  useEffect(() => {
    if (!sessionDetails?.session_id) {
      return;
    }

    if (lastSessionIdRef.current !== sessionDetails.session_id) {
      lastSessionIdRef.current = sessionDetails.session_id;
      isDestroyingRef.current = false;
      moduleTeardownComplete = false;
    }
  }, [sessionDetails?.session_id]);

  useEffect(() => {
    const initiateAudioCall = async () => {
      if (!sessionDetails?.session_token) {
        return;
      }

      if (
        moduleTeardownComplete ||
        sessionStatus === SessionStatus.Completed ||
        sessionStatus === SessionStatus.Terminated
      ) {
        return;
      }

      // Module-level guards prevent a second Device from being created when
      // React StrictMode double-mounts Shell, or when sessionDetails changes
      // multiple times before the async token fetch resolves.
      if (getTwilioAudioDevice() || moduleIsInitializing) {
        return;
      }
      moduleIsInitializing = true;

      try {
        const response = await getAudioToken();
        const token = response.data?.token as string;

        if (token && isAudioConfigurationEnabled) {
          startTwilioAudioCall(token);
        }
      } catch (error: unknown) {
        handleError(error);
      } finally {
        moduleIsInitializing = false;
      }
    };

    initiateAudioCall();
  }, [getAudioToken, isAudioConfigurationEnabled, sessionDetails, sessionStatus]);

  useEffect(() => {
    document.addEventListener("StopProctorClient", teardownTwilioAudio);
    document.addEventListener("Terminated", teardownTwilioAudio);

    return () => {
      document.removeEventListener("StopProctorClient", teardownTwilioAudio);
      document.removeEventListener("Terminated", teardownTwilioAudio);
    };
  }, []);

  return [];
};

export default useTwilioCall;
